import '../../../packages/neb-lit-components/src/components/neb-header';
import '../../../packages/neb-lit-components/src/components/neb-pagination';

import { LitElement, html, css } from 'lit';

import { getSubscriptionPayments } from '../../../packages/neb-api-client/src/payments/scheduled-payments-api-client';
import { getPaymentByElectronicPaymentId } from '../../../packages/neb-api-client/src/payments-api-client';
import {
  openOverlay,
  OVERLAY_KEYS,
} from '../../../packages/neb-lit-components/src/utils/overlay-constants';
import {
  SCHEDULE_RUN_STATUS,
  SUBSCRIPTION_PAYMENT_OPTION,
} from '../../../packages/neb-utils/enums';
import { getSubscriptionCharges } from '../../api-clients/subscriptions';
import { CSS_SPACING } from '../../styles';
import { openPayment } from '../../utils/payment-util';
import {
  STATUS,
  TABLE_CONFIG_SUBSCRIPTION_PAYMENTS,
  TABLE_CONFIG_SUBSCRIPTION_CHARGES,
} from '../tables/neb-table-subscription-payment-history';

export const ELEMENTS = {
  header: {
    id: 'header',
  },
  table: {
    id: 'table',
  },
  pagination: {
    id: 'pagination',
  },
};

export const EMPTY_MESSAGES = {
  DEFAULT: 'There is no payment history for this subscription',
  LOADING: 'Loading...',
  ERROR: 'Error loading subscription payment history',
};

export const PAGE_SIZE = 10;

class NebPanelSubscriptionPaymentHistory extends LitElement {
  static get properties() {
    return {
      patientId: { type: String },
      patientPackageId: { type: String },
      subscriptionPaymentOption: { type: Number },
      __tableData: { type: Array },
      __emptyMessage: { type: String },
      __currentPage: { type: Number },
      __pageSize: { type: Number },
    };
  }

  constructor() {
    super();
    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.patientId = '';
    this.patientPackageId = '';
    this.subscriptionPaymentOption = SUBSCRIPTION_PAYMENT_OPTION.AUTOMATIC;
    this.__tableData = [];
    this.__emptyMessage = EMPTY_MESSAGES.DEFAULT;
    this.__currentPage = 0;
    this.__pageSize = PAGE_SIZE;
  }

  __initHandlers() {
    this.__handlers = {
      onSelectCharge: async (_, item) => {
        if ([STATUS.POSTED, STATUS.PAID].includes(item.status)) {
          await openOverlay(OVERLAY_KEYS.LEDGER_VIEW_SELECTED_CHARGES, {
            patient: { id: item.patientId },
            lineItemIds: [item.lineItemId],
            selectedIds: [item.lineItemId],
            closeOnSave: true,
          });
        }
      },

      onSelectPayment: async (_, item) => {
        if (item.status !== STATUS.PAID) {
          return;
        }

        const paidRun = item.run.find(
          ({ status }) => status === SCHEDULE_RUN_STATUS.PAID,
        );

        if (paidRun?.transactionId) {
          const payment = await getPaymentByElectronicPaymentId(
            paidRun.transactionId,
          );

          await openPayment({ payment, readonly: false });
        }
      },

      onPageChanged: page => {
        this.__currentPage = page;
      },
    };
  }

  get __paginatedTableData() {
    const startIndex = this.__currentPage * this.__pageSize;
    const endIndex = startIndex + this.__pageSize;
    return this.__tableData.slice(startIndex, endIndex);
  }

  get __pageCount() {
    return Math.ceil(this.__tableData.length / this.__pageSize);
  }

  async firstUpdated() {
    this.__emptyMessage = EMPTY_MESSAGES.LOADING;

    try {
      this.__tableData =
        this.subscriptionPaymentOption === SUBSCRIPTION_PAYMENT_OPTION.MANUAL
          ? await getSubscriptionCharges(this.patientPackageId)
          : await getSubscriptionPayments(
              this.patientId,
              this.patientPackageId,
            );

      this.__emptyMessage = EMPTY_MESSAGES.DEFAULT;
    } catch (error) {
      this.__tableData = [];
      this.__emptyMessage = EMPTY_MESSAGES.ERROR;
    }
  }

  static get styles() {
    return css`
      .table {
        padding-top: ${CSS_SPACING};
        padding-bottom: ${CSS_SPACING};
      }

      .pagination {
        flex-direction: row-reverse;
        padding-top: ${CSS_SPACING};
        padding-bottom: ${CSS_SPACING};
        padding-right: 10px;
      }
    `;
  }

  render() {
    return html`
      <neb-header
        id="${ELEMENTS.header.id}"
        label="Payment History"
      ></neb-header>

      <neb-table-subscription-payment-history
        id="${ELEMENTS.table.id}"
        class="table"
        .config="${this.subscriptionPaymentOption ===
        SUBSCRIPTION_PAYMENT_OPTION.MANUAL
          ? TABLE_CONFIG_SUBSCRIPTION_CHARGES
          : TABLE_CONFIG_SUBSCRIPTION_PAYMENTS}"
        .model="${this.__paginatedTableData}"
        .emptyMessage="${this.__emptyMessage}"
        .onSelectRow="${this.subscriptionPaymentOption ===
        SUBSCRIPTION_PAYMENT_OPTION.MANUAL
          ? this.__handlers.onSelectCharge
          : this.__handlers.onSelectPayment}"
      ></neb-table-subscription-payment-history>

      <neb-pagination
        id="${ELEMENTS.pagination.id}"
        class="pagination"
        .pageCount="${this.__pageCount}"
        .currentPage="${this.__currentPage}"
        .onPageChanged="${this.__handlers.onPageChanged}"
      ></neb-pagination>
    `;
  }
}

customElements.define(
  'neb-panel-subscription-payment-history',
  NebPanelSubscriptionPaymentHistory,
);
